import React, { useEffect, useRef, useState } from 'react';
import { ShieldCheck, X } from 'lucide-react';
import { allocateUserTokens, listUsers, moderateUser, setUserAccountType } from '../services/adminService';

function TokenAllocation({ target, busy, onAllocate }) {
  const [amount, setAmount] = useState('');
  const allocation = useRef(null);
  async function submit(event) {
    event.preventDefault();
    const tokens = Number(amount);
    if (busy || !Number.isSafeInteger(tokens) || tokens < 1 || tokens > 1000000000) return;
    if (!allocation.current || allocation.current.amount !== tokens) allocation.current = {amount:tokens, id:window.crypto.randomUUID()};
    if (await onAllocate(target, tokens, allocation.current.id)) { setAmount(''); allocation.current = null; }
  }
  return <form className="admin-token-allocation" onSubmit={submit}>
    <span className="admin-token-balance">{(target.tokenBalance || 0).toLocaleString()} tokens</span>
    <input type="number" min="1" max="1000000000" step="1" required value={amount} onChange={event => setAmount(event.target.value)} disabled={busy} placeholder="Tokens to add" aria-label={`Tokens to add for ${target.email || target.displayName || target.uid}`} />
    <button className="admin-button approve" disabled={busy || !amount} type="submit">Add tokens</button>
  </form>;
}

export default function SuperadminModal({ user, onClose, onUsersChanged }) {
  const ref = useRef(null);
  const [users, setUsers] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [removing, setRemoving] = useState(null);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => { const dialog = ref.current; dialog.showModal(); return () => dialog.close(); }, []);
  useEffect(() => {
    let active = true;
    setLoading(true); setError('');
    listUsers().then(result => { if (active) { setUsers(result.users); setCursor(result.cursor); } })
      .catch(failure => { if (active) setError(failure.message || 'Could not load users.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [refresh]);
  async function loadMore() {
    setBusy(true); setError('');
    try { const result = await listUsers(cursor); setUsers(current => [...current, ...result.users]); setCursor(result.cursor); }
    catch (failure) { setError(failure.message || 'Could not load users.'); }
    finally { setBusy(false); }
  }
  async function addTokens(target, amount, allocationId) {
    if (busy) return false;
    setBusy(true); setError(''); setMessage('');
    try {
      const result = await allocateUserTokens(target.uid, amount, allocationId);
      setUsers(current => current.map(item => item.uid === target.uid ? { ...item, tokenBalance: result.tokenBalance } : item));
      setMessage(`${amount.toLocaleString()} tokens added to ${target.displayName || target.email || 'user'}.`);
      return true;
    } catch (failure) { setError(failure.message || 'Could not add tokens.'); return false; }
    finally { setBusy(false); }
  }
  async function changeAccountType(target, accountType) {
    if (busy || accountType === (target.accountType || 'FREE')) return;
    setBusy(true); setError(''); setMessage('');
    try {
      const result = await setUserAccountType(target.uid, accountType);
      setUsers(current => current.map(item => item.uid === target.uid ? { ...item, accountType: result.accountType } : item));
      setMessage(`${target.displayName || target.email || 'User'} account updated to ${result.accountType}.`);
    } catch (failure) { setError(failure.message || 'Could not update account type.'); }
    finally { setBusy(false); }
  }
  async function act(target, action) {
    setBusy(true); setError(''); setMessage('');
    try {
      const result = await moderateUser(target.uid, action);
      setUsers(current => current.map(item => item.uid === target.uid ? { ...item, status: result.status } : item));
      setRemoving(null); setMessage(`${target.displayName || target.email || 'User'}: ${result.status}.`);
      onUsersChanged?.();
    } catch (failure) { setError(failure.message || 'Could not update access.'); }
    finally { setBusy(false); }
  }
  const visible = users.filter(item => (filter === 'all' || item.status === filter) && `${item.displayName} ${item.email}`.toLowerCase().includes(search.toLowerCase()));
  return <dialog ref={ref} className="profile-dialog admin-dialog canvas-panel-scroll" aria-labelledby="superadmin-title" onCancel={event => { event.preventDefault(); if (!busy) onClose(); }}>
    <header className="profile-dialog-header"><div><span className="profile-kicker">ACCESS MANAGEMENT</span><h2 id="superadmin-title"><ShieldCheck size={22} />Superadmin</h2></div><button className="profile-close" onClick={onClose} disabled={busy} aria-label="Close superadmin"><X size={20} /></button></header>
    <p className="profile-description">Approve new signups and manage access to ParrotSuite.</p>
    <div className="admin-filters"><input aria-label="Search loaded users" placeholder="Search loaded users..." value={search} onChange={event => setSearch(event.target.value)} />
      <select aria-label="Account status" value={filter} onChange={event => setFilter(event.target.value)}>{['all', 'pending', 'active', 'paused', 'removed'].map(status => <option key={status} value={status}>{status === 'all' ? 'All statuses' : status[0].toUpperCase() + status.slice(1)}</option>)}</select>
      <button className="admin-button" disabled={busy || loading} onClick={() => { setRemoving(null); setRefresh(value => value + 1); }}>Refresh</button></div>
    {error && <p className="profile-feedback error" role="alert">{error}</p>}{message && <p className="profile-feedback success" role="status">{message}</p>}
    {loading ? <p role="status" className="admin-empty">Loading users...</p> : <>
      <p className="admin-count">{visible.length} shown / {users.length} loaded{cursor ? ' / More available' : ''}</p>
      <ul className="admin-users">{visible.map(target => <li key={target.uid}>
        <div className="admin-user-details"><strong>{target.displayName || 'Unnamed user'}{target.uid === user.uid ? ' (you)' : ''}</strong><span>{target.email || target.uid}</span></div>
        <span className={'admin-status ' + target.status}>{target.status}</span>
        <label className="admin-account-type">Account<select aria-label={`Account type for ${target.email || target.displayName || target.uid}`} value={target.accountType || 'FREE'} disabled={busy} onChange={event => changeAccountType(target, event.target.value)}><option value="FREE">FREE</option><option value="PRO">PRO</option></select></label>
        <div className="admin-user-actions">{target.role === 'superadmin' ? <span className="admin-status">Superadmin</span> : target.uid !== user.uid && target.status !== 'removed' && <>
          {target.status === 'pending' && <button className="admin-button approve" disabled={busy} onClick={() => act(target, 'approve')}>Approve</button>}
          {target.status === 'active' && <button className="admin-button" disabled={busy} onClick={() => act(target, 'pause')}>Pause</button>}
          {target.status === 'paused' && <button className="admin-button approve" disabled={busy} onClick={() => act(target, 'resume')}>Resume</button>}
          <button className="admin-button danger" disabled={busy} onClick={() => setRemoving(target)}>Remove</button>
        </>}</div>
        <TokenAllocation target={target} busy={busy} onAllocate={addTokens} />
      </li>)}</ul>
      {!visible.length && <p className="admin-empty">No users match this view.</p>}{cursor && <button className="admin-button" disabled={busy} onClick={loadMore}>Load more users</button>}
    </>}
    {removing && <section className="admin-confirm" aria-label="Confirm removal"><p>Remove {removing.email || removing.displayName} from ParrotSuite? They will lose access. Their saved work is retained. This cannot be undone here.</p><div className="profile-actions"><button className="admin-button" disabled={busy} onClick={() => setRemoving(null)}>Cancel</button><button className="admin-button danger" disabled={busy} onClick={() => act(removing, 'remove')}>{busy ? 'Removing...' : 'Confirm removal'}</button></div></section>}
  </dialog>;
}
